import { useState } from 'react';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import useAppSelector from 'hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import { returnSelectors } from 'store/reducers/returnSlice';
import { styled } from 'styled-components';
import CreditNoteModal from './CreditNoteModal';

const DEV_MOCK_PREFIX = 'DEV-MOCK';

const CreditNotesCard = () => {
    const { t } = useTranslation();
    const creditNotes = useAppSelector(returnSelectors.selectCreditNotes);
    const orderNumbers = useAppSelector(returnSelectors.selectSelectedOrderNumbers);
    const [selectedOrderNumber, setSelectedOrderNumber] = useState<string | null>(null);
    const [isDevMock, setIsDevMock] = useState(false);

    const formatDate = (date?: string | null) => {
        if (!date) return '';
        return new Date(date).toLocaleDateString();
    };

    const handleCreditNoteClick = (orderNumber: string) => {
        // Check if this is a dev mock credit note
        if (orderNumber.startsWith(DEV_MOCK_PREFIX)) {
            // Use first order number from the return instead
            const fallbackOrderNumber = orderNumbers[0] ?? null;
            setSelectedOrderNumber(fallbackOrderNumber);
            setIsDevMock(true);
        } else {
            setSelectedOrderNumber(orderNumber);
            setIsDevMock(false);
        }
    };

    const handleCloseModal = () => {
        setSelectedOrderNumber(null);
        setIsDevMock(false);
    };

    if (!creditNotes || creditNotes.length === 0) {
        return null;
    }

    return (
        <>
            <Container $grow={1} column className="credit-notes-wrapper">
                <Text as="label" xBold fontSize={11} className="mb-1" padding={0} margin={0}>
                    {t('creditNotes')}
                </Text>
                <Wrapper column>
                    <Flex column gap="0.5rem">
                        {creditNotes.map((creditNote, index) => {
                            const orderNumber = creditNote.orderNumber ?? '';
                            return (
                                <CreditNoteItem key={`credit-note-${index}`}>
                                    <CreditNoteButton type="button" onClick={() => handleCreditNoteClick(orderNumber)}>
                                        <BolderText as="span">
                                            {orderNumber}
                                            {creditNote.createdDate && (
                                                <StyledText thin as="span">
                                                    {` (${formatDate(creditNote.createdDate)})`}
                                                </StyledText>
                                            )}
                                        </BolderText>
                                    </CreditNoteButton>
                                </CreditNoteItem>
                            );
                        })}
                    </Flex>
                </Wrapper>
            </Container>

            <CreditNoteModal
                open={selectedOrderNumber !== null}
                onClose={handleCloseModal}
                orderNumber={selectedOrderNumber}
                isDevMock={isDevMock}
            />
        </>
    );
};

export default CreditNotesCard;

const Container = styled(Flex)``;

const Wrapper = styled(Flex)`
    height: 100%;
    background-color: ${({ theme }) => theme.colors.fillWhite};
    padding: 1rem;
    border-radius: var(--radius);
`;

const BolderText = styled(Text)`
    font-size: 12px;
    font-weight: 600;
`;

const StyledText = styled(Text)`
    font-size: 12px;
    font-weight: 400;
`;

const CreditNoteItem = styled(Flex)`
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight ?? '#eee'};
    padding-bottom: 0.5rem;

    &:last-child {
        border-bottom: none;
        padding-bottom: 0;
    }
`;

const CreditNoteButton = styled.button`
    align-items: center;
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.link};
    cursor: pointer;
    display: flex;
    padding: 0;
    text-decoration: underline;
    text-align: left;

    &:hover {
        opacity: 0.7;
    }
`;
